import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { useAuthStore } from './auth-store';

// ─── Types ────────────────────────────────────────────────────────────────────

type SidebarMode = 'full' | 'collapsed' | 'hidden';

interface SidebarState {
  mode: SidebarMode;
  overlayOpen: boolean;
  drawerOpen: boolean;
  /** Legutóbbi 3 menüpont ID (NavItem.id) */
  recentItems: string[];

  // Actions
  toggleCollapse: () => void;
  openOverlay: () => void;
  closeOverlay: () => void;
  openDrawer: () => void;
  closeDrawer: () => void;
  /** Legutóbbi navigáció rögzítése (max 3 elem, újak elöl) */
  trackNavigation: (menuItemId: string) => void;
}

// ─── Tenant-scoped storage (F7 fix) ──────────────────────────────────────────

/** LocalStorage wrapper: kulcs = 'kgc-sidebar-{tenantId}' — tenant-szintű izoláció */
const tenantScopedStorage = createJSONStorage(() => ({
  getItem: (name: string) => {
    const tenantId = useAuthStore.getState().user?.tenantId ?? 'default';
    return localStorage.getItem(`${name}-${tenantId}`);
  },
  setItem: (name: string, value: string) => {
    const tenantId = useAuthStore.getState().user?.tenantId ?? 'default';
    localStorage.setItem(`${name}-${tenantId}`, value);
  },
  removeItem: (name: string) => {
    const tenantId = useAuthStore.getState().user?.tenantId ?? 'default';
    localStorage.removeItem(`${name}-${tenantId}`);
  },
}));

// ─── Store ────────────────────────────────────────────────────────────────────

export const useSidebarStore = create<SidebarState>()(
  persist(
    (set, get) => ({
      mode: 'full',
      overlayOpen: false,
      drawerOpen: false,
      recentItems: [],

      toggleCollapse: () =>
        set(state => ({
          mode: state.mode === 'full' ? 'collapsed' : 'full',
        })),

      openOverlay: () => set({ overlayOpen: true }),
      closeOverlay: () => set({ overlayOpen: false }),

      openDrawer: () => set({ drawerOpen: true }),
      closeDrawer: () => set({ drawerOpen: false }),

      trackNavigation: (menuItemId: string) => {
        const current = get().recentItems;
        // Új elem elöl, ismétlés eltávolítva, max 3
        const updated = [menuItemId, ...current.filter(id => id !== menuItemId)].slice(0, 3);
        set({ recentItems: updated });
      },
    }),
    {
      name: 'kgc-sidebar',
      storage: tenantScopedStorage,
      // Csak állapot perzisztálódik, a drawer/overlay session-only
      partialize: state => ({
        mode: state.mode,
        recentItems: state.recentItems,
      }),
    }
  )
);
